import { Sofa, Armchair, Boxes, Route, PackageCheck, Lock, Layers, Users, HeartHandshake, Tag, MessageSquareText, ArrowRight, Cross } from "lucide-react";
import { Reveal, Hud } from "./Reveal";

const RATES = [
  { icon: Sofa, name: "Single-Item Haul", price: "$99", from: true, slug: "single" },
  { icon: Armchair, name: "2–3 Furniture Items", price: "$149", from: true, slug: "two-three", featured: true },
  { icon: Boxes, name: "Small Room or Apartment Haul", price: "$249", from: true, slug: "small-room" },
  { icon: Route, name: "Large, Multi-Stop, or Long-Distance Haul", price: "Custom Quote", from: false, slug: "custom" },
];

const INCLUDES = [
  { icon: PackageCheck, text: "Careful loading and unloading" },
  { icon: Lock, text: "Secure transportation" },
  { icon: Layers, text: "Moving blankets and tie-downs" },
  { icon: Users, text: "A two-person crew when required" },
  { icon: HeartHandshake, text: "Respectful handling of every item" },
  { icon: Tag, text: "Upfront pricing before work begins" },
];

export const Hauling = () => (
  <section id="hauling" data-testid="hauling-section" className="relative bg-panel py-24 md:py-32">
    <div className="hazard absolute left-0 top-0 h-3 w-full" aria-hidden="true" />
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <p data-testid="hauling-label" className="mb-6 inline-flex items-stretch border-2 border-gold">
          <span className="bg-gold px-3 py-1.5 font-heading text-base uppercase tracking-wide text-ink">Service 02</span>
          <span className="px-4 py-1.5 font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Furniture & Item Hauling</span>
        </p>
        <Hud>We Move It For You</Hud>
        <h2 className="mt-5 font-heading text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
          Furniture & Item Hauling: <span className="text-gold">Careful, Reliable, Affordable.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-base font-medium capitalize leading-relaxed text-warmgray md:text-lg">
          Not junk — items you're keeping. Whether someone needs furniture transported, an appliance delivered, or several
          large items moved across town, Godspeed provides dependable hauling throughout Metro Atlanta. We handle the
          lifting, loading, transportation, and unloading from start to finish.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <p data-testid="hauling-rates-label" className="mt-14 flex items-center gap-3 font-body text-xs font-bold uppercase tracking-[0.25em] text-white/70">
          <span className="hazard-thin h-2 w-8" aria-hidden="true" />
          Introductory Hauling Rates
        </p>
      </Reveal>

      <div className="mt-6 grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {RATES.map((r, i) => (
          <Reveal key={r.slug} delay={i * 0.06} className="h-full">
            <div
              data-testid={`hauling-card-${r.slug}`}
              className={`relative flex h-full flex-col p-7 transition-all duration-200 hover:-translate-y-1 ${
                r.featured ? "border-2 border-gold bg-ink shadow-hard-gold" : "grit-card"
              }`}
            >
              {r.featured && <div className="hazard-thin absolute left-0 top-0 h-2 w-full" aria-hidden="true" />}
              <span className={`flex h-14 w-14 items-center justify-center border-2 border-gold/50 bg-ink ${r.featured ? "mt-2" : ""}`}>
                <r.icon size={26} strokeWidth={1.5} className="text-gold" />
              </span>
              <p className="mt-6 font-body text-xs font-bold uppercase leading-snug tracking-[0.2em] text-white">{r.name}</p>
              <p data-testid={`hauling-price-${r.slug}`} className="mt-5 font-heading uppercase leading-none tracking-wide">
                {r.from && <span className="mr-2 font-body text-xs font-bold uppercase tracking-[0.15em] text-warmgray">Starting at</span>}
                <span className={`block text-gold ${r.from ? "mt-2 text-5xl md:text-6xl" : "mt-2 text-3xl md:text-4xl"}`}>{r.price}</span>
              </p>
              <span className="mt-auto block pt-6">
                <span className={`block h-1 ${r.featured ? "hazard-thin w-full" : "w-10 bg-gold/50"}`} aria-hidden="true" />
              </span>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div data-testid="hauling-includes" className="mt-14 border-2 border-coal bg-ink p-7 md:p-10">
          <p className="font-heading text-2xl uppercase tracking-wide text-gold md:text-3xl">Every Haul Includes:</p>
          <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDES.map((item) => (
              <li key={item.text} className="flex items-center gap-3">
                <item.icon size={18} strokeWidth={1.8} className="shrink-0 text-gold" />
                <span className="font-body text-sm font-bold uppercase tracking-[0.08em] text-white/85">{item.text}</span>
              </li>
            ))}
          </ul>
          <p data-testid="hauling-pricing-notice" className="mt-8 max-w-3xl border-t-2 border-coal pt-6 text-xs capitalize leading-relaxed text-warmgray">
            Final pricing depends on the number and size of items, travel distance, stairs or elevator access, number of
            stops, and labor required. Oversized or extremely heavy items may cost additional.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-14 flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <a
              href="#contact"
              data-testid="hauling-quote-button"
              className="btn-brutal shadow-hard-gold inline-flex items-center gap-3 bg-gold px-10 py-4 font-heading text-2xl uppercase tracking-wide text-ink"
            >
              Get A Fast Hauling Quote <ArrowRight size={20} strokeWidth={2.5} />
            </a>
            <p data-testid="hauling-text-instructions" className="mt-5 flex max-w-xl items-start gap-3 text-sm font-medium capitalize leading-relaxed text-white/80">
              <MessageSquareText size={18} className="mt-0.5 shrink-0 text-gold" />
              <span>
                Text photos of the items, pickup and delivery ZIP codes, and any access details to{" "}
                <a href="sms:4706840150" className="font-bold text-gold underline-offset-4 hover:underline">470-684-0150</a>.
              </span>
            </p>
          </div>
          <p data-testid="hauling-tagline" className="flex items-center gap-3 font-heading text-2xl uppercase tracking-wide text-white md:text-3xl">
            <Cross size={22} strokeWidth={2} className="text-gold" />
            Built On Faith. <span className="text-gold">Driven By Service.</span>
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
